import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  DailyEntryRecord,
  TrackerSettingsRecord,
  TrackerSummaryRecord,
} from '../types/dailyBite';
import type { ObservationRecord } from '../types/observations';
import {
  ALL_CORRELATION_IDS,
  CorrelationCardRecord,
  CorrelationId,
  CorrelationsDocRecord,
  DEFAULT_CORRELATION_OPTINS,
  EMPTY_CORRELATIONS_DOC,
  THREE_WEEK_THRESHOLD,
} from '../types/correlations';

/* ────────────────────────────────────────────────────────────────────────────
   Phase 3 — correlation unlocks.

   This service mirrors the weeklyReflectionService pattern: a pure(ish)
   compute that takes pre-fetched inputs (entries, settings, summary,
   wearable observations) and writes a single projection doc at
   users/{uid}/correlations/current. Called lazily on /today open and
   fire-and-forget after every daily entry save.

   Threshold rule: every real correlation card is gated behind ≥ 3 distinct
   ISO weeks of logged data in the compute window. Below threshold we still
   emit a card for each opted-in correlation, but with status 'gathering'
   and a "N of 3 weeks" hint. Never shown past threshold: anything that
   reads as causal.

   Editorial rule baked into every headline + body string in this file:
   "looks like" / "appears to" / "worth watching" / "too early to say".
   Never "X causes Y". Never pass/fail. Alcohol and nicotine are never
   moralised — we observe, we don't judge.
   ──────────────────────────────────────────────────────────────────────── */

const COMPUTE_WINDOW_DAYS = 28;
const MIN_BUCKET_DAYS = 5;

/* ────────────────────────────────────────────────────────────────────────────
   Doc refs + helpers
   ──────────────────────────────────────────────────────────────────────── */

function correlationsRef(uid: string) {
  return doc(db, 'users', uid, 'correlations', 'current');
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map((part) => Number(part));
  return new Date(y, (m || 1) - 1, d || 1);
}

function addDays(date: Date, delta: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
}

function isoWeekId(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const isoYear = d.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const weekNum = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${isoYear}-W${pad(weekNum)}`;
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}

/* ────────────────────────────────────────────────────────────────────────────
   Wearable observation helpers — we only need a thin slice: HRV + sleep
   metrics keyed to a local date. Wearable observedAt strings are ISO
   timestamps; we strip to the local date so we can index by daily_entries
   date key.
   ──────────────────────────────────────────────────────────────────────── */

interface WearableDailyIndex {
  hrv: Map<string, number>;
  sleepEfficiency: Map<string, number>;
  sleepDurationHours: Map<string, number>;
}

function observationDateKey(observation: ObservationRecord): string | null {
  if (!observation.observedAt) return null;
  // observedAt is either an ISO string or a YYYY-MM-DD date. parse permissively.
  const parsed = new Date(observation.observedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return toDateKey(parsed);
}

function isHrvObservation(obs: ObservationRecord): boolean {
  const label = (obs.display || '').toLowerCase();
  if (!label) return false;
  return (
    label.includes('heart rate variability') ||
    label.includes('hrv') ||
    label === 'rmssd'
  );
}

function indexWearable(observations: ObservationRecord[] | undefined): WearableDailyIndex {
  const index: WearableDailyIndex = {
    hrv: new Map(),
    sleepEfficiency: new Map(),
    sleepDurationHours: new Map(),
  };
  if (!observations || observations.length === 0) return index;

  for (const obs of observations) {
    const key = observationDateKey(obs);
    if (!key) continue;
    const value = typeof obs.valueNumber === 'number' ? obs.valueNumber : null;
    if (value == null || Number.isNaN(value)) continue;

    const display = (obs.display || '').toLowerCase();
    if (isHrvObservation(obs)) {
      // Multiple HRV observations per day get averaged — keep it simple.
      const prev = index.hrv.get(key);
      index.hrv.set(key, prev == null ? value : (prev + value) / 2);
    } else if (display.includes('sleep efficiency')) {
      index.sleepEfficiency.set(key, value);
    } else if (display.includes('sleep duration')) {
      index.sleepDurationHours.set(key, value);
    }
  }
  return index;
}

/* ────────────────────────────────────────────────────────────────────────────
   Window + coverage helpers
   ──────────────────────────────────────────────────────────────────────── */

interface WindowStats {
  windowKeys: string[];
  entriesByDate: Map<string, DailyEntryRecord>;
  daysCovered: number;
  weeksCovered: number;
  weeksWithCoverage: Set<string>;
}

function buildWindow(entries: DailyEntryRecord[], now: Date): WindowStats {
  const windowKeys: string[] = [];
  for (let i = COMPUTE_WINDOW_DAYS - 1; i >= 0; i -= 1) {
    windowKeys.push(toDateKey(addDays(now, -i)));
  }
  const byDate = new Map<string, DailyEntryRecord>();
  for (const e of entries) {
    if (e.date && windowKeys.indexOf(e.date) !== -1) byDate.set(e.date, e);
  }

  // An ISO week "counts toward threshold" only if the member logged at
  // least 3 days in it. Lighter weeks still count toward daysCovered but
  // aren't enough on their own to trip the 3-week threshold.
  const weekDayCounts = new Map<string, number>();
  let daysCovered = 0;
  for (const key of windowKeys) {
    const entry = byDate.get(key);
    if (!entry) continue;
    if ((entry.completeness || 0) <= 0) continue;
    daysCovered += 1;
    const wId = isoWeekId(parseDateKey(key));
    weekDayCounts.set(wId, (weekDayCounts.get(wId) || 0) + 1);
  }
  const weeksWithCoverage = new Set<string>();
  weekDayCounts.forEach((count, wId) => {
    if (count >= 3) weeksWithCoverage.add(wId);
  });

  return {
    windowKeys,
    entriesByDate: byDate,
    daysCovered,
    weeksCovered: Math.min(weeksWithCoverage.size, THREE_WEEK_THRESHOLD),
    weeksWithCoverage,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Editorial copy.

   Every card has copy for every status we might surface. Written in
   /today voice: observe, hedge, keep agency with the member. These strings
   are the spec's "Phase 3 editorial rules" in code form — if you're
   changing them, re-read the spec first.
   ──────────────────────────────────────────────────────────────────────── */

interface CardCopyVariant {
  headline: string;
  body: string;
}

function gatheringCopy(weeksCovered: number): CardCopyVariant {
  const remaining = Math.max(0, THREE_WEEK_THRESHOLD - weeksCovered);
  return {
    headline: `Still gathering — ${weeksCovered} of ${THREE_WEEK_THRESHOLD} weeks in`,
    body:
      remaining === 0
        ? 'Almost there. Another handful of logged days and this card will have something worth reading.'
        : `We hold these until there are at least three weeks of data to look at. Keep logging when it's natural — ${remaining === 1 ? 'one more week' : `${remaining} more weeks`} to go.`,
  };
}

function insufficientCopy(label: string): CardCopyVariant {
  return {
    headline: `Too early to say on ${label.toLowerCase()}`,
    body: 'We have the weeks, but not enough logged days on both sides of the pattern yet. It\'ll settle as you keep using Daily Bite.',
  };
}

function missingWearableCopy(): CardCopyVariant {
  return {
    headline: 'Waiting on a wearable sync',
    body: 'This one needs HRV data from your wearable. Once a sync lands we\'ll start watching how drinking days line up with the next morning\'s recovery number.',
  };
}

function formatEffectSigned(value: number, suffix: string): string {
  const rounded = Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(rounded)}${suffix}`;
}

/* ────────────────────────────────────────────────────────────────────────────
   Per-card compute. Each returns a CorrelationCardRecord already stamped
   with status + copy. When the member has opted out we skip compute and
   emit an 'opted_out' card — cheap, makes the settings UX reversible with
   no waiting.
   ──────────────────────────────────────────────────────────────────────── */

function optedOutCard(id: CorrelationId, window: WindowStats): CorrelationCardRecord {
  return {
    id,
    status: 'opted_out',
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    headline: 'Off in your settings',
    body: 'You\'ve turned this observation off. Turn it back on in Daily Bite settings whenever you want to see it again.',
  };
}

function gatheringCard(id: CorrelationId, window: WindowStats): CorrelationCardRecord {
  const copy = gatheringCopy(window.weeksCovered);
  return {
    id,
    status: 'gathering',
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    headline: copy.headline,
    body: copy.body,
  };
}

/* ── caffeine ↔ sleep ────────────────────────────────────────────────────── */

function computeCaffeineSleep(
  window: WindowStats,
  wearable: WearableDailyIndex,
): CorrelationCardRecord {
  const base: CorrelationCardRecord = {
    id: 'caffeine_sleep',
    status: 'insufficient_signal',
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    headline: '',
    body: '',
  };

  const lateEff: number[] = [];
  const earlyEff: number[] = [];

  for (const key of window.windowKeys) {
    const entry = window.entriesByDate.get(key);
    if (!entry?.caffeine || (entry.caffeine.totalMg || 0) <= 0) continue;
    const eff = wearable.sleepEfficiency.get(key);
    if (eff == null) continue;
    const lastAt = entry.caffeine.lastAt;
    if (!lastAt || typeof (lastAt as Timestamp).toDate !== 'function') continue;
    const hour = (lastAt as Timestamp).toDate().getHours();
    if (hour >= 15) lateEff.push(eff);
    else earlyEff.push(eff);
  }

  base.highBucketDays = lateEff.length;
  base.lowBucketDays = earlyEff.length;

  if (lateEff.length < MIN_BUCKET_DAYS || earlyEff.length < MIN_BUCKET_DAYS) {
    // Either no wearable sleep data, or not enough contrast in caffeine timing.
    if (wearable.sleepEfficiency.size === 0) {
      const copy = missingWearableCopy();
      return { ...base, headline: copy.headline.replace('drinking days', 'late-caffeine days').replace('HRV', 'sleep efficiency'), body: 'This one needs sleep data from your wearable. Once a sync lands we\'ll start watching how late-caffeine days line up with that night\'s sleep efficiency.' };
    }
    const copy = insufficientCopy('caffeine × sleep');
    return { ...base, headline: copy.headline, body: copy.body };
  }

  const delta = mean(lateEff) - mean(earlyEff);
  base.effect = Number(delta.toFixed(2));

  if (delta < -2) {
    base.status = 'observed';
    base.direction = 'inverse';
    base.headline = 'Looks like late-caffeine days sleep a touch lighter';
    base.body = `On the ${lateEff.length} days you had caffeine after 3pm, sleep efficiency averaged ${formatEffectSigned(delta, '%')} versus your earlier-caffeine days. It's only a pattern, not a prescription — worth watching if it holds.`;
    return base;
  }
  if (delta > 2) {
    base.status = 'observed';
    base.direction = 'associated';
    base.headline = 'Late caffeine and your sleep look unbothered';
    base.body = `Across ${lateEff.length} late-caffeine days, sleep efficiency didn't land lower than on your earlier-caffeine days. Useful context — not everyone metabolises caffeine the same way.`;
    return base;
  }

  base.status = 'observed';
  base.direction = 'flat';
  base.headline = 'No obvious pull between caffeine timing and sleep';
  base.body = `Across the days with wearable sleep data, late-caffeine nights and earlier ones are landing in a similar range. Worth checking back in a few weeks.`;
  return base;
}

/* ── caffeine ↔ next-day mood ────────────────────────────────────────────── */

function computeCaffeineMoodNextDay(window: WindowStats): CorrelationCardRecord {
  const base: CorrelationCardRecord = {
    id: 'caffeine_mood_nextday',
    status: 'insufficient_signal',
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    headline: '',
    body: '',
  };

  const lateMood: number[] = [];
  const earlyMood: number[] = [];

  for (let i = 0; i < window.windowKeys.length - 1; i += 1) {
    const todayKey = window.windowKeys[i];
    const tomorrowKey = window.windowKeys[i + 1];
    const todayEntry = window.entriesByDate.get(todayKey);
    const tomorrowEntry = window.entriesByDate.get(tomorrowKey);
    if (!todayEntry?.caffeine || (todayEntry.caffeine.totalMg || 0) <= 0) continue;
    if (!tomorrowEntry?.mood || !tomorrowEntry.mood.loggedAt) continue;
    const lastAt = todayEntry.caffeine.lastAt;
    if (!lastAt || typeof (lastAt as Timestamp).toDate !== 'function') continue;
    const hour = (lastAt as Timestamp).toDate().getHours();
    const valence = tomorrowEntry.mood.valence ?? 0;
    if (hour >= 15) lateMood.push(valence);
    else earlyMood.push(valence);
  }

  base.highBucketDays = lateMood.length;
  base.lowBucketDays = earlyMood.length;

  if (lateMood.length < MIN_BUCKET_DAYS || earlyMood.length < MIN_BUCKET_DAYS) {
    const copy = insufficientCopy('caffeine × next-day mood');
    return { ...base, headline: copy.headline, body: copy.body };
  }

  const delta = mean(lateMood) - mean(earlyMood);
  base.effect = Number(delta.toFixed(2));

  if (delta < -0.15) {
    base.status = 'observed';
    base.direction = 'inverse';
    base.headline = 'Next-day mood tends to run a bit flatter after late caffeine';
    base.body = `After the ${lateMood.length} days you had caffeine past 3pm, the following day's mood read a little lower on valence. It's only a pattern; it could be caffeine, it could be what those days looked like. Worth watching.`;
    return base;
  }
  if (delta > 0.15) {
    base.status = 'observed';
    base.direction = 'associated';
    base.headline = 'Next-day mood looks unaffected by late caffeine';
    base.body = `On the days following later caffeine, mood isn't landing lower than on other days. Useful context to have.`;
    return base;
  }

  base.status = 'observed';
  base.direction = 'flat';
  base.headline = 'Caffeine timing isn\'t showing up in next-day mood';
  base.body = `Across the window, next-day mood is sitting in a similar range regardless of when your last caffeine was. Worth another look in a few weeks.`;
  return base;
}

/* ── alcohol ↔ HRV ───────────────────────────────────────────────────────── */

function computeAlcoholHrv(
  window: WindowStats,
  wearable: WearableDailyIndex,
): CorrelationCardRecord {
  const base: CorrelationCardRecord = {
    id: 'alcohol_hrv',
    status: 'insufficient_signal',
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    headline: '',
    body: '',
  };

  if (wearable.hrv.size === 0) {
    const copy = missingWearableCopy();
    return { ...base, headline: copy.headline, body: copy.body };
  }

  const drinkingHrv: number[] = [];
  const dryHrv: number[] = [];

  for (let i = 0; i < window.windowKeys.length - 1; i += 1) {
    const todayKey = window.windowKeys[i];
    const tomorrowKey = window.windowKeys[i + 1];
    const todayEntry = window.entriesByDate.get(todayKey);
    if (!todayEntry?.alcohol) continue;
    const nextHrv = wearable.hrv.get(tomorrowKey);
    if (nextHrv == null) continue;
    const units = todayEntry.alcohol.units || 0;
    if (units > 0) drinkingHrv.push(nextHrv);
    else dryHrv.push(nextHrv);
  }

  base.highBucketDays = drinkingHrv.length;
  base.lowBucketDays = dryHrv.length;

  if (drinkingHrv.length < MIN_BUCKET_DAYS || dryHrv.length < MIN_BUCKET_DAYS) {
    const copy = insufficientCopy('alcohol × recovery');
    return { ...base, headline: copy.headline, body: copy.body };
  }

  // Observe the gap without moralising. Framed as "recovery tends to",
  // not "drinking damages recovery". The card deliberately reports
  // whichever direction the data shows rather than assuming one.
  const delta = mean(drinkingHrv) - mean(dryHrv);
  base.effect = Number(delta.toFixed(1));

  if (delta < -3) {
    base.status = 'observed';
    base.direction = 'inverse';
    base.headline = 'Recovery tends to read lower after drinking days';
    base.body = `On the mornings after your ${drinkingHrv.length} drinking days, HRV averaged ${formatEffectSigned(delta, '')} versus the mornings after dry days. One of the more consistent signals in the wearable literature — worth watching against how the rest of the day feels.`;
    return base;
  }
  if (delta > 3) {
    base.status = 'observed';
    base.direction = 'associated';
    base.headline = 'Drinking days aren\'t pulling your HRV down';
    base.body = `Your HRV on mornings after drinking is landing in the same range as dry days. Individual metabolism varies — observing this rather than assuming.`;
    return base;
  }

  base.status = 'observed';
  base.direction = 'flat';
  base.headline = 'Drinking and HRV aren\'t moving together';
  base.body = `Across the wearable data we have, drinking days and dry days are landing in the same HRV range. Keep logging and we'll keep watching.`;
  return base;
}

/* ── protein ↔ satiety ───────────────────────────────────────────────────── */

function computeProteinSatiety(window: WindowStats): CorrelationCardRecord {
  const base: CorrelationCardRecord = {
    id: 'protein_satiety',
    status: 'insufficient_signal',
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    headline: '',
    body: '',
  };

  // Protein satiety signal proxy: next-day mood valence (is the member
  // steadier) combined with gratitude response length (is the day getting
  // engaged with). No single number is the "answer"; we look at the
  // same-day pattern across the two.
  const highProteinMood: number[] = [];
  const lowProteinMood: number[] = [];
  const highProteinGratitudeChars: number[] = [];
  const lowProteinGratitudeChars: number[] = [];

  for (const key of window.windowKeys) {
    const entry = window.entriesByDate.get(key);
    if (!entry?.protein || (entry.protein.totalGrams || 0) <= 0) continue;
    const grams = entry.protein.totalGrams;

    if (entry.mood && entry.mood.loggedAt) {
      if (grams >= 100) highProteinMood.push(entry.mood.valence ?? 0);
      else if (grams < 70) lowProteinMood.push(entry.mood.valence ?? 0);
    }
    if (entry.gratitude && entry.gratitude.response) {
      const len = entry.gratitude.response.trim().length;
      if (grams >= 100) highProteinGratitudeChars.push(len);
      else if (grams < 70) lowProteinGratitudeChars.push(len);
    }
  }

  base.highBucketDays = highProteinMood.length;
  base.lowBucketDays = lowProteinMood.length;

  if (highProteinMood.length < MIN_BUCKET_DAYS || lowProteinMood.length < MIN_BUCKET_DAYS) {
    const copy = insufficientCopy('protein × satiety');
    return { ...base, headline: copy.headline, body: copy.body };
  }

  const moodDelta = mean(highProteinMood) - mean(lowProteinMood);
  const gratitudeDelta = mean(highProteinGratitudeChars) - mean(lowProteinGratitudeChars);
  base.effect = Number(moodDelta.toFixed(2));

  if (moodDelta > 0.15 || gratitudeDelta > 15) {
    base.status = 'observed';
    base.direction = 'associated';
    base.headline = 'Higher-protein days look a little steadier';
    base.body = `On the days you hit around 100g of protein, mood valence and the depth of the evening reflection both ran a touch higher than your lower-protein days. Could be the protein, could be that those are just structured days — worth watching.`;
    return base;
  }
  if (moodDelta < -0.15) {
    base.status = 'observed';
    base.direction = 'inverse';
    base.headline = 'Protein isn\'t tracking with steadier days yet';
    base.body = "Higher-protein days aren't landing with steadier mood yet. Protein is one input among many — keep logging and we'll see what settles.";
    return base;
  }

  base.status = 'observed';
  base.direction = 'flat';
  base.headline = 'No obvious lift from higher-protein days yet';
  base.body = `Mood and reflection depth are sitting in a similar range across protein levels so far. Worth checking back after another week or two.`;
  return base;
}

/* ── mood ↔ digestion ────────────────────────────────────────────────────── */

function computeMoodDigestion(window: WindowStats): CorrelationCardRecord {
  const base: CorrelationCardRecord = {
    id: 'mood_digestion',
    status: 'insufficient_signal',
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    headline: '',
    body: '',
  };

  // We count the rate of off-pattern Bristol entries (1–2 or 6–7) across
  // low-mood days vs. higher-mood days, on the same day and the day after.
  // Never directional in copy — the gut-brain axis runs both ways and the
  // spec explicitly flags this as "observation, not claim".
  let lowMoodDays = 0;
  let highMoodDays = 0;
  let lowMoodOffDigest = 0;
  let highMoodOffDigest = 0;

  const offBristol = (entry: DailyEntryRecord | undefined): boolean => {
    if (!entry?.digestion?.movements?.length) return false;
    return entry.digestion.movements.some((m) => m.bristol <= 2 || m.bristol >= 6);
  };

  for (let i = 0; i < window.windowKeys.length; i += 1) {
    const key = window.windowKeys[i];
    const entry = window.entriesByDate.get(key);
    if (!entry?.mood || !entry.mood.loggedAt) continue;
    const valence = entry.mood.valence ?? 0;
    const nextEntry =
      i + 1 < window.windowKeys.length
        ? window.entriesByDate.get(window.windowKeys[i + 1])
        : undefined;
    const off = offBristol(entry) || offBristol(nextEntry);
    if (valence <= -0.15) {
      lowMoodDays += 1;
      if (off) lowMoodOffDigest += 1;
    } else if (valence >= 0.15) {
      highMoodDays += 1;
      if (off) highMoodOffDigest += 1;
    }
  }

  base.highBucketDays = lowMoodDays;
  base.lowBucketDays = highMoodDays;

  if (lowMoodDays < MIN_BUCKET_DAYS || highMoodDays < MIN_BUCKET_DAYS) {
    const copy = insufficientCopy('mood × digestion');
    return { ...base, headline: copy.headline, body: copy.body };
  }

  const lowRate = lowMoodOffDigest / lowMoodDays;
  const highRate = highMoodOffDigest / highMoodDays;
  const delta = (lowRate - highRate) * 100;
  base.effect = Number(delta.toFixed(1));

  if (Math.abs(delta) < 15) {
    base.status = 'observed';
    base.direction = 'flat';
    base.headline = 'Mood and digestion are moving independently';
    base.body = "Your off-pattern digestion days aren't clustering with your lower-mood days this window. Observing this rather than reading into it.";
    return base;
  }

  base.status = 'observed';
  base.direction = delta > 0 ? 'associated' : 'inverse';
  base.headline = 'Mood and digestion are showing up together';
  base.body = `On your lower-mood days in this window, digestion was off-pattern ${Math.round(lowRate * 100)}% of the time versus ${Math.round(highRate * 100)}% on steadier days. The gut-brain axis runs both ways — we're noting the pattern, not claiming a cause.`;
  return base;
}

/* ────────────────────────────────────────────────────────────────────────────
   Orchestration + save
   ──────────────────────────────────────────────────────────────────────── */

export interface ComputeCorrelationsInput {
  uid: string;
  /** Pre-fetched entries optional — speeds up the /today render path. */
  entries?: DailyEntryRecord[];
  settings: TrackerSettingsRecord;
  summary: TrackerSummaryRecord;
  /**
   * Pre-fetched wearable observations. When UNDEFINED the service treats
   * this as "caller didn't check wearable data" and preserves any
   * previously-computed wearable-backed card (e.g. alcohol × HRV from the
   * /intelligence recompute). When passed as an EMPTY ARRAY the service
   * treats that as "checked and there's nothing" and renders the
   * waiting-on-sync copy. This split is deliberate: the /today save path
   * doesn't load wearable observations, and we don't want it to quietly
   * overwrite a good HRV card with a placeholder.
   */
  wearableObservations?: ObservationRecord[];
}

export async function computeAndSaveCorrelations(
  input: ComputeCorrelationsInput,
): Promise<CorrelationsDocRecord> {
  const now = new Date();
  const optIns = {
    ...DEFAULT_CORRELATION_OPTINS,
    ...(input.settings.correlations || {}),
  };

  let entries = input.entries;
  if (!entries) {
    const windowStart = toDateKey(addDays(now, -(COMPUTE_WINDOW_DAYS - 1)));
    const snap = await getDocs(
      query(
        collection(db, 'users', input.uid, 'daily_entries'),
        where('date', '>=', windowStart),
      ),
    );
    entries = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as DailyEntryRecord));
  }

  const window = buildWindow(entries, now);
  const wearable = indexWearable(input.wearableObservations);
  const wearableProvided = input.wearableObservations !== undefined;

  // Cards that depend on wearable data. When a save-path recompute doesn't
  // pass wearables we keep the previous values so a good HRV card from
  // /intelligence doesn't get stomped back to "waiting on a sync".
  const WEARABLE_BACKED: Set<CorrelationId> = new Set(['alcohol_hrv', 'caffeine_sleep']);
  let previous: CorrelationsDocRecord | null = null;
  if (!wearableProvided) {
    try {
      const snap = await getDoc(correlationsRef(input.uid));
      if (snap.exists()) {
        previous = snap.data() as CorrelationsDocRecord;
      }
    } catch {
      previous = null;
    }
  }
  const previousById = new Map<CorrelationId, CorrelationCardRecord>();
  if (previous?.cards) {
    for (const card of previous.cards) previousById.set(card.id, card);
  }

  const cards: CorrelationCardRecord[] = ALL_CORRELATION_IDS.map((id) => {
    if (!optIns[id]) return optedOutCard(id, window);

    // Preserve a previous wearable-backed observed card on save-path
    // recomputes where we weren't given the observations to recompute it.
    if (!wearableProvided && WEARABLE_BACKED.has(id)) {
      const prev = previousById.get(id);
      if (prev && prev.status === 'observed') return prev;
    }

    if (window.weeksCovered < THREE_WEEK_THRESHOLD) return gatheringCard(id, window);
    switch (id) {
      case 'caffeine_sleep':
        return computeCaffeineSleep(window, wearable);
      case 'caffeine_mood_nextday':
        return computeCaffeineMoodNextDay(window);
      case 'alcohol_hrv':
        return computeAlcoholHrv(window, wearable);
      case 'protein_satiety':
        return computeProteinSatiety(window);
      case 'mood_digestion':
        return computeMoodDigestion(window);
      default:
        return gatheringCard(id, window);
    }
  });

  const nowTs = Timestamp.fromDate(now);
  const next: CorrelationsDocRecord = {
    cards,
    windowDays: COMPUTE_WINDOW_DAYS,
    weeksCovered: window.weeksCovered,
    daysCovered: window.daysCovered,
    wearableAvailable:
      wearable.hrv.size > 0 ||
      wearable.sleepEfficiency.size > 0 ||
      wearable.sleepDurationHours.size > 0,
    lastComputedAt: nowTs,
    lastComputedForDate: toDateKey(now),
  };

  await setDoc(
    correlationsRef(input.uid),
    {
      ...next,
      lastComputedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return next;
}

export async function getCorrelations(
  uid: string,
): Promise<CorrelationsDocRecord> {
  const snap = await getDoc(correlationsRef(uid));
  if (!snap.exists()) return { ...EMPTY_CORRELATIONS_DOC };
  const data = snap.data() as Partial<CorrelationsDocRecord>;
  return {
    ...EMPTY_CORRELATIONS_DOC,
    ...data,
    cards: data.cards || [],
  };
}

// Re-export the orderBy helper so the /intelligence surface can use it if
// we ever need to list historical snapshots (none yet; current is a
// single projection).
export { orderBy };
